import { StyleSheet } from "react-native";

import type { UiTheme } from "../theme";

// The ONE Home box literal (hairline border + r3 + s5 gutter + s4 stack
// rhythm) every boxed Home element composes: the glance cards below and the
// hero wrapper in HomeView (HR-9 — its 2nd consumer, extracted so the box
// can't drift into copies again). Deliberately NOT the CN-3 <Card>
// primitive — just this surface's single source; the full primitive still
// lands with CN-3.
export function makeHomeBoxStyle(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r } = theme;
  return {
    marginHorizontal: s.s5,
    marginBottom: s.s4,
    borderRadius: r.r3,
    borderColor: p.border,
    borderWidth: 1,
  } as const;
}

// The shared chrome of the Home "glanceable" cards (readiness badge, weekly
// stats): tappable row-card with an eyebrow title, a content column and a
// trailing › chevron. Extracted when WeeklyStatsCard became the 2nd verbatim
// copy of FormBadgeCard's sheet (HR-S4 review).
export function makeGlanceCardStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    card: {
      ...makeHomeBoxStyle(theme),
      flexDirection: "row",
      alignItems: "center",
      padding: s.s4,
      gap: s.s3,
    },
    pressed: { opacity: 0.7 },
    // minWidth:0 so a row's text column can shrink/wrap normally instead of
    // pushing past its flex share (yoga's row default is minWidth:auto).
    textCol: { flex: 1, gap: 2, minWidth: 0 },
    chevron: { color: p.ink3, fontSize: t.sizes.xl },
  });
}

// The shared chrome of the Home quick-ENTRY cards (weigh-in, glucose): the
// same Home box, but a wrapping row that ends in a small field + save button
// instead of a chevron. QuickWeightCard and QuickGlucoseCard held two
// byte-identical copies of this sheet; only their comments differed.
//
// A sibling of makeGlanceCardStyles, not a variant of it: the glance cards
// are tappable rows that navigate, these take input. What they DO share —
// the box literal — they share through makeHomeBoxStyle, the same way.
export function makeQuickEntryCardStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    card: {
      ...makeHomeBoxStyle(theme),
      flexDirection: "row",
      alignItems: "center",
      // Wraps rather than overflowing a narrow card.
      flexWrap: "wrap",
      padding: s.s3,
      backgroundColor: p.surface,
      gap: s.s3,
    },
    textCol: { flex: 1, gap: 2, minWidth: 120 },
    label: { color: p.ink, fontSize: t.sizes.base, fontWeight: "700" },
    // Hint carries meaningful secondary text in every state — the "Saved ✓"
    // confirmation and the prompt to log — so it sits at the AA floor (ink2),
    // not the decorative-caption ink3. (Written for the weigh-in card; it
    // holds for the glucose card, whose hint plays the same two roles.)
    hint: { color: p.ink2, fontSize: t.sizes.sm },
    // The actual current value is data Antenore reads → AA contrast.
    // (Mono is skipped here: the string mixes a "Latest:" label with the
    // number; mono-ifying the label would be wrong and splitting the runs
    // is a structural change beyond this display-only pass.)
    hintValue: { color: p.ink2 },
    // Layout only — box internals (36 · paddingVertical:0 · border · font) now
    // live in the <TextField>/<Button> primitives (design-system consolidation).
    input: { minWidth: 72 },
    saveBtn: { minWidth: 72 },
  });
}
